import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Upload, Users, CheckCircle2, XCircle, AlertTriangle, Loader2, FileJson,
  ChevronDown,
} from "lucide-react";

export default function SuperImportCustomers() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [lastMode, setLastMode] = useState(null);   // "dry" | "real"
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/api/super-admin/tenants").then(({ data }) => {
      setTenants(data.tenants || []);
    }).catch(() => toast.error("Failed to load tenants"));
  }, []);

  const selectedTenant = tenants.find((t) => t.id === tenantId) || null;

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setResult(null);
  };

  const submit = async (dryRun) => {
    if (!tenantId) { toast.error("Select a tenant first"); return; }
    if (!file) { toast.error("Choose a JSON file"); return; }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25MB)"); return;
    }
    setBusy(true);
    setResult(null);
    setLastMode(dryRun ? "dry" : "real");
    try {
      const fd = new FormData();
      fd.append("tenant_id", tenantId);
      fd.append("file", file);
      const { data } = await api.post(
        `/api/super-admin/import/customers?dry_run=${dryRun ? "true" : "false"}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      if (data.ok === false) {
        toast.error(data.error || "Import failed");
      } else if (dryRun) {
        toast.success(`Dry-run OK — would import ${data.imported} of ${data.total_rows}`);
      } else {
        toast.success(`Imported ${data.imported} of ${data.total_rows} customers`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setLastMode(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-6 page-enter max-w-3xl" data-testid="super-import-customers-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Data Migration</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Upload className="w-7 h-7 text-brand-ink"/> Import Customers
        </h1>
        <p className="text-sm text-neutral-600 mt-1">
          Bulk-import customer JSON exported from a legacy MongoDB (e.g. Compass export).
          Duplicates by phone are skipped; addresses without pincode are dropped;
          <code className="mx-1 px-1.5 py-0.5 bg-brand-cream rounded text-[11px]">91XXXXXXXXXX</code>
          is auto-normalized to
          <code className="mx-1 px-1.5 py-0.5 bg-brand-cream rounded text-[11px]">+91XXXXXXXXXX</code>.
        </p>
      </div>

      {/* Step 1 — tenant */}
      <div className="bg-white border border-neutral-200 rounded-3xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-brand-ink text-white grid place-content-center text-sm font-extrabold">1</div>
          <div className="font-heading text-lg font-extrabold">Select target tenant</div>
        </div>
        <div className="relative">
          <select
            data-testid="super-import-tenant-select"
            value={tenantId}
            onChange={(e) => { setTenantId(e.target.value); setResult(null); }}
            className="w-full h-11 rounded-2xl border border-neutral-200 bg-brand-cream/40 pl-4 pr-10 text-sm font-bold appearance-none focus:outline-none focus:ring-2 focus:ring-brand-ink"
          >
            <option value="">— choose a tenant —</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name} · {t.slug}</option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"/>
        </div>
        {selectedTenant && (
          <div className="text-xs text-neutral-600" data-testid="super-import-tenant-hint">
            All imported customers will get <code className="px-1.5 py-0.5 bg-brand-cream rounded text-[11px]">tenant_id={selectedTenant.id}</code>
          </div>
        )}
      </div>

      {/* Step 2 — file */}
      <div className="bg-white border border-neutral-200 rounded-3xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-brand-ink text-white grid place-content-center text-sm font-extrabold">2</div>
          <div className="font-heading text-lg font-extrabold">Upload JSON file</div>
        </div>
        <label className="block cursor-pointer">
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            onChange={onFile}
            className="hidden"
            data-testid="super-import-file-input"
          />
          <div className="border-2 border-dashed border-neutral-300 rounded-2xl p-6 text-center hover:border-brand-ink transition-colors">
            <FileJson className="w-8 h-8 mx-auto text-neutral-400 mb-2"/>
            {file ? (
              <div>
                <div className="font-bold text-sm" data-testid="super-import-file-name">{file.name}</div>
                <div className="text-xs text-neutral-500">{(file.size / 1024).toFixed(1)} KB</div>
                <div className="text-[11px] text-neutral-400 mt-1">Click to choose a different file</div>
              </div>
            ) : (
              <div>
                <div className="font-bold text-sm">Click to select a .json file</div>
                <div className="text-xs text-neutral-500 mt-0.5">Array, NDJSON, or MongoDB Extended JSON (max 25 MB)</div>
              </div>
            )}
          </div>
        </label>
      </div>

      {/* Step 3 — run */}
      <div className="bg-white border border-neutral-200 rounded-3xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-brand-ink text-white grid place-content-center text-sm font-extrabold">3</div>
          <div className="font-heading text-lg font-extrabold">Preview then import</div>
        </div>
        <p className="text-xs text-neutral-600">
          Start with <b>Dry-run</b> to preview counts without writing. Then <b>Import for real</b> when the numbers look right.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => submit(true)}
            disabled={busy || !file || !tenantId}
            data-testid="super-import-dry-run-btn"
            className="h-11 rounded-2xl font-bold border-2"
          >
            {busy && lastMode === "dry" ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <AlertTriangle className="w-4 h-4 mr-2"/>}
            Dry-run (no writes)
          </Button>
          <Button
            onClick={() => submit(false)}
            disabled={busy || !file || !tenantId}
            data-testid="super-import-submit-btn"
            className="h-11 rounded-2xl bg-brand-ink hover:bg-neutral-800 text-white font-bold"
          >
            {busy && lastMode === "real" ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Upload className="w-4 h-4 mr-2"/>}
            Import for real
          </Button>
          {(file || result) && (
            <Button variant="ghost" onClick={reset} disabled={busy} className="h-11 rounded-2xl" data-testid="super-import-reset-btn">
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Result */}
      {result && (
        <div
          className={`rounded-3xl p-5 border-2 ${
            result.ok === false ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"
          }`}
          data-testid="super-import-result"
        >
          <div className="flex items-center gap-2 mb-3">
            {result.ok === false ? (
              <XCircle className="w-6 h-6 text-red-600"/>
            ) : (
              <CheckCircle2 className="w-6 h-6 text-green-600"/>
            )}
            <div className="font-heading text-lg font-extrabold">
              {result.ok === false ? "Import failed" : (lastMode === "dry" ? "Dry-run results" : "Import complete")}
            </div>
          </div>

          {result.ok === false ? (
            <div className="text-sm text-red-800 font-mono" data-testid="super-import-error">
              {result.error}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Total in file" value={result.total_rows} testid="stat-total"/>
                <Stat label="Imported" value={result.imported} testid="stat-imported" tone="good"/>
                <Stat label="Duplicate phone" value={result.skipped_duplicate_phone} testid="stat-dupes"/>
                <Stat label="No phone" value={result.skipped_no_phone} testid="stat-no-phone"/>
                <Stat label="No name" value={result.skipped_no_name} testid="stat-no-name"/>
                <Stat label="Addr imported" value={result.addresses_imported} testid="stat-addr-in"/>
                <Stat label="Addr skipped" value={result.addresses_skipped} testid="stat-addr-skip"/>
                <Stat label="Errors" value={(result.errors || []).length} testid="stat-errors"/>
              </div>

              {result.sample_imported?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-bold mb-1">Sample imported (first 3)</div>
                  <ul className="text-xs space-y-1" data-testid="super-import-sample">
                    {result.sample_imported.map((s, i) => (
                      <li key={i} className="bg-white rounded-lg px-3 py-1.5 border border-neutral-200 flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-neutral-500"/>
                        <b>{s.name}</b>
                        <span className="text-neutral-500">· {s.phone}</span>
                        <span className="text-neutral-500">· {s.address_count} addr</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.errors?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-red-700 font-bold mb-1">
                    Row errors (showing first {result.errors.length})
                  </div>
                  <ul className="text-xs space-y-1 font-mono" data-testid="super-import-row-errors">
                    {result.errors.map((e, i) => (
                      <li key={i} className="bg-white rounded-lg px-3 py-1 border border-red-200">
                        row {e.row}: {e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, tone }) {
  return (
    <div className="bg-white rounded-2xl px-3 py-2 border border-neutral-200" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">{label}</div>
      <div className={`font-heading text-2xl font-extrabold ${tone === "good" ? "text-green-700" : "text-brand-ink"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}
